import { useEffect, useState } from "react";
import { downloadIssuedReport } from "../api/client";
import { ApiError } from "../api/client";

/**
 * The audit client's copy of the issued report — M9-0 phase 6, open item 71.
 *
 * **A screen rather than a bare download, and the reason is what `issued` means.** Releasing
 * the report is not the certification decision — that is the certification body's, taken
 * outside this platform — and a link that dropped a PDF into the downloads folder with no
 * words around it would let *your report has arrived* be read as *you are certified*. The
 * sentence is on the screen, and it is the same sentence the message carries.
 *
 * It holds a capability and no account, exactly as the portal does, so there is no shell
 * around it and nothing to sign into. The link reaches one report and nothing else.
 */
export function IssuedReport({ token }: { token: string }) {
  const [state, setState] = useState<"ready" | "working" | "done" | "gone">("ready");
  const [error, setError] = useState<string | null>(null);

  // The fragment is cleared once it has been read. A live credential left in the address
  // bar is a credential in a browser history and in every screenshot of it.
  useEffect(() => {
    history.replaceState(null, "", location.pathname);
  }, []);

  const download = async () => {
    setError(null);
    setState("working");
    try {
      const file = await downloadIssuedReport(token);
      const url = URL.createObjectURL(file);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "audit-report.pdf";
      anchor.click();
      URL.revokeObjectURL(url);
      setState("done");
    } catch (cause) {
      // 401 means the link was replaced; 403 means the report is no longer issued. Both
      // are answered by the certification body, not by retrying here.
      const gone = cause instanceof ApiError && [401, 403].includes(cause.problem.status);
      setError(
        cause instanceof ApiError ? cause.message : "The report could not be downloaded.",
      );
      setState(gone ? "gone" : "ready");
    }
  };

  return (
    <main className="signin">
      <div className="signin__panel">
        <div className="signin__brand">
          <div className="mark" aria-hidden="true">
            c
          </div>
          <div>
            <div className="signin__wm">complao</div>
            <div className="tag">AUDIT PLATFORM</div>
          </div>
        </div>
        <p className="signin__tagline">
          Your certification body has issued the report for your Stage&nbsp;1 initial
          certification audit.
        </p>

        {error ? (
          <p className="problem" role="alert">
            {error}
          </p>
        ) : null}

        {state === "gone" ? (
          <p className="signin__tagline">
            Ask your certification body for a new link. This one no longer reaches the
            report.
          </p>
        ) : (
          <div className="signin__actions">
            <button
              type="button"
              className="btn"
              onClick={() => void download()}
              disabled={state === "working"}
            >
              {state === "working" ? "Preparing…" : "Download the report (PDF)"}
            </button>
          </div>
        )}

        {state === "done" ? (
          <p className="callout green" role="status">
            Downloaded. Keep your copy &mdash; this link reaches one report and may be
            replaced.
          </p>
        ) : null}

        <p className="signin__tagline" style={{ marginTop: "18px" }}>
          Issuing the report releases it to you. <b>It is not a certification decision.</b>{" "}
          Whether your organisation is certified is decided by your certification body, and
          they will tell you separately.
        </p>
      </div>
    </main>
  );
}
